import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../Button";
import { Loader } from "../Loader";
import { VisaAdminService } from "../../api/generated/services/VisaAdminService";
import { TransactionHistoryItem } from "../../api/generated/models/TransactionHistoryItem";
import { TransactionFailure } from "../../api/generated/models/TransactionFailure";
import { TransactionSuccess } from "../../api/generated/models/TransactionSuccess";
import { ROUTES } from "../../config/routes";
import "../../api/openApiConfig";

const PAGE_SIZE = 20;
// The backend has no date filter or ordering, so the full list is fetched once
// per tab and filtered/sorted/paged client-side.
const FETCH_PAGE_SIZE = 500;

type TabType = "failed" | "successful";

type SortKey =
  | "date"
  | "errorMessage"
  | "identityId"
  | "businessId"
  | "txTimestamp"
  | "cardLast4"
  | "amount";

type SortDir = "asc" | "desc";

const DEFAULT_SORT: { key: SortKey; dir: SortDir } = { key: "date", dir: "desc" };

// Text columns read naturally A→Z on the first click; dates and amounts newest/largest first.
const TEXT_SORT_KEYS: SortKey[] = ["errorMessage", "identityId", "businessId", "cardLast4"];

interface ParsedJsonData {
  identityId?: string;
  contentfulBusinessId?: string;
  transactionTimestamp?: string;
  cardNumberLastFour?: string;
  transactionAmount?: number;
  transactionCurrency?: string;
}

interface Row {
  item: TransactionHistoryItem;
  eventTimestamp: string | undefined;
  errorMessage: string;
  json: ParsedJsonData;
}

const parseJsonSentToBackend = (json: string | null | undefined): ParsedJsonData => {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    return {
      identityId: parsed.identityId || undefined,
      contentfulBusinessId: parsed.contentfulBusinessId || undefined,
      transactionTimestamp: parsed.transactionTimestamp || undefined,
      cardNumberLastFour: parsed.cardNumberLastFour || undefined,
      transactionAmount: parsed.transactionAmount || undefined,
      transactionCurrency: parsed.transactionCurrency || undefined,
    };
  } catch {
    return {};
  }
};

const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("da-DK", {
      timeZone: "Europe/Copenhagen",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return dateString;
  }
};

const formatAmount = (amount: number | undefined, currency: string | undefined): string => {
  if (amount === undefined) return "-";
  const currencyStr = currency || "";
  return `${amount.toFixed(2)} ${currencyStr}`.trim();
};

// "YYYY-MM-DD" in Copenhagen time, comparable with the <input type="date"> values.
const toCopenhagenDateString = (dateString: string | undefined): string | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("sv-SE", { timeZone: "Europe/Copenhagen" });
};

const getLastFailure = (item: TransactionHistoryItem): TransactionFailure | undefined => {
  if (!item.failures || item.failures.length === 0) return undefined;
  return item.failures[item.failures.length - 1];
};

const getLastSuccess = (item: TransactionHistoryItem): TransactionSuccess | undefined => {
  if (!item.successes || item.successes.length === 0) return undefined;
  return item.successes[item.successes.length - 1];
};

const sortValue = (row: Row, key: SortKey): number | string => {
  switch (key) {
    case "date":
      return Date.parse(row.eventTimestamp || "") || 0;
    case "txTimestamp":
      return Date.parse(row.json.transactionTimestamp || "") || 0;
    case "amount":
      return row.json.transactionAmount ?? Number.NEGATIVE_INFINITY;
    case "errorMessage":
      return row.errorMessage;
    case "identityId":
      return row.json.identityId || "";
    case "businessId":
      return row.json.contentfulBusinessId || "";
    case "cardLast4":
      return row.json.cardNumberLastFour || "";
  }
};

const csvEscape = (value: string): string => `"${value.replace(/"/g, '""')}"`;

const TransactionHistorySection: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const activeTab: TabType = statusParam === "failed" ? "failed" : "successful";

  const [transactionsByTab, setTransactionsByTab] = useState<
    Record<TabType, TransactionHistoryItem[] | null>
  >({ failed: null, successful: null });
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [successfulCount, setSuccessfulCount] = useState<number | null>(null);
  const [failedCount, setFailedCount] = useState<number | null>(null);

  const fetchCounts = useCallback(async () => {
    try {
      const [successResult, failedResult] = await Promise.all([
        VisaAdminService.postApiVisaAdminListTransactionHistory({
          page: 1,
          pageSize: 1,
          status: ["Succeeded"],
        }),
        VisaAdminService.postApiVisaAdminListTransactionHistory({
          page: 1,
          pageSize: 1,
          status: ["Failed"],
        }),
      ]);
      setSuccessfulCount(successResult.totalItems || 0);
      setFailedCount(failedResult.totalItems || 0);
    } catch (err) {
      console.error("Error fetching counts:", err);
    }
  }, []);

  const fetchAllTransactions = useCallback(async (tab: TabType) => {
    try {
      setLoading(true);
      setError(null);
      const statusFilter = tab === "failed" ? ["Failed"] : ["Succeeded"];
      const items: TransactionHistoryItem[] = [];
      let currentPage = 1;
      let hasNext = true;
      while (hasNext) {
        const result = await VisaAdminService.postApiVisaAdminListTransactionHistory({
          page: currentPage,
          pageSize: FETCH_PAGE_SIZE,
          status: statusFilter,
        });
        items.push(...(result.items || []));
        hasNext = result.hasNextPage || false;
        currentPage += 1;
        setLoadProgress({ loaded: items.length, total: result.totalItems || items.length });
      }
      setTransactionsByTab((prev) => ({ ...prev, [tab]: items }));
    } catch (err) {
      setError("Failed to fetch transaction history");
      console.error("Error fetching transaction history:", err);
    } finally {
      setLoading(false);
      setLoadProgress(null);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    if (transactionsByTab[activeTab] === null) {
      fetchAllTransactions(activeTab);
    } else {
      setLoading(false);
    }
  }, [activeTab, transactionsByTab, fetchAllTransactions]);

  const rows = useMemo<Row[]>(() => {
    const all = transactionsByTab[activeTab] || [];
    return all.map((item) => {
      const lastFailure = activeTab === "failed" ? getLastFailure(item) : undefined;
      const lastSuccess = activeTab === "successful" ? getLastSuccess(item) : undefined;
      return {
        item,
        eventTimestamp:
          (activeTab === "failed" ? lastFailure?.failedAt : lastSuccess?.succeededAt) ||
          item.lastUpdatedAt ||
          undefined,
        errorMessage: lastFailure?.errorMessage || "",
        json: parseJsonSentToBackend(
          activeTab === "failed"
            ? lastFailure?.jsonSentToPrimaryBackend
            : lastSuccess?.jsonSentToPrimaryBackend
        ),
      };
    });
  }, [transactionsByTab, activeTab]);

  // Date range is inclusive Copenhagen days; sorting covers the filtered set.
  const filteredRows = useMemo(() => {
    const filtered =
      dateFrom || dateTo
        ? rows.filter((row) => {
            const day = toCopenhagenDateString(row.eventTimestamp);
            if (!day) return false;
            if (dateFrom && day < dateFrom) return false;
            if (dateTo && day > dateTo) return false;
            return true;
          })
        : rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const aValue = sortValue(a, sort.key);
      const bValue = sortValue(b, sort.key);
      const cmp =
        typeof aValue === "string" && typeof bValue === "string"
          ? aValue.localeCompare(bValue)
          : (aValue as number) - (bValue as number);
      return cmp * dir;
    });
  }, [rows, dateFrom, dateTo, sort]);

  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE);
  const pageRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleTabChange = (tab: TabType) => {
    setSearchParams({ status: tab === "failed" ? "failed" : "success" });
    setSort(DEFAULT_SORT);
    setPage(1);
  };

  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    setPage(1);
  };

  const handleDateToChange = (value: string) => {
    setDateTo(value);
    setPage(1);
  };

  const handleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: TEXT_SORT_KEYS.includes(key) ? "asc" : "desc" }
    );
    setPage(1);
  };

  const handleRefresh = () => {
    fetchCounts();
    fetchAllTransactions(activeTab);
  };

  const handleExportCsv = () => {
    const header =
      activeTab === "failed"
        ? ["Failed At", "Error Message", "Identity ID", "Business ID", "Tx Timestamp", "Card Last 4", "Amount", "Currency", "Unique Transaction ID"]
        : ["Sent At", "Identity ID", "Business ID", "Tx Timestamp", "Card Last 4", "Amount", "Currency", "Unique Transaction ID"];
    const csvRows = filteredRows.map((row) => {
      const common = [
        row.json.identityId || "",
        row.json.contentfulBusinessId || "",
        formatDateTime(row.json.transactionTimestamp),
        row.json.cardNumberLastFour || "",
        row.json.transactionAmount !== undefined ? row.json.transactionAmount.toFixed(2) : "",
        row.json.transactionCurrency || "",
        row.item.uniqueTransactionId || "",
      ];
      return activeTab === "failed"
        ? [formatDateTime(row.eventTimestamp), row.errorMessage, ...common]
        : [formatDateTime(row.eventTimestamp), ...common];
    });
    const csv = [header, ...csvRows]
      .map((columns) => columns.map(csvEscape).join(","))
      .join("\r\n");
    // BOM so Excel opens the file as UTF-8.
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const range = [dateFrom && `from-${dateFrom}`, dateTo && `to-${dateTo}`].filter(Boolean).join("-");
    link.download = `visa-${activeTab}-transactions${range ? `-${range}` : ""}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderSortableHeader = (label: string, key: SortKey) => (
    <th
      onClick={() => handleSort(key)}
      className="cursor-pointer select-none whitespace-nowrap"
      aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === key && <span aria-hidden="true">{sort.dir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </th>
  );

  const renderDetailsCell = (row: Row) => (
    <td className="whitespace-nowrap">
      <Link
        to={`${ROUTES.ADMIN_VISA_TRANSACTION()}?uniqueTransactionId=${encodeURIComponent(row.item.uniqueTransactionId || "")}`}
      >
        <Button variant="outline" size="sm">
          Details
        </Button>
      </Link>
    </td>
  );

  const renderFailedTable = () => (
    <div className="overflow-x-auto">
      <table className="table-default">
        <thead>
          <tr>
            {renderSortableHeader("Failed At", "date")}
            {renderSortableHeader("Error Message", "errorMessage")}
            {renderSortableHeader("Identity ID", "identityId")}
            {renderSortableHeader("Business ID", "businessId")}
            {renderSortableHeader("Tx Timestamp", "txTimestamp")}
            {renderSortableHeader("Card Last 4", "cardLast4")}
            {renderSortableHeader("Amount", "amount")}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row, index) => (
            <tr key={row.item.uniqueTransactionId || index}>
              <td className="whitespace-nowrap">{formatDateTime(row.eventTimestamp)}</td>
              <td className="whitespace-nowrap">{row.errorMessage || "-"}</td>
              <td className="font-mono text-xs whitespace-nowrap">{row.json.identityId || "-"}</td>
              <td className="font-mono text-xs whitespace-nowrap">{row.json.contentfulBusinessId || "-"}</td>
              <td className="whitespace-nowrap">{formatDateTime(row.json.transactionTimestamp)}</td>
              <td className="font-mono whitespace-nowrap">{row.json.cardNumberLastFour || "-"}</td>
              <td className="whitespace-nowrap">
                {formatAmount(row.json.transactionAmount, row.json.transactionCurrency)}
              </td>
              {renderDetailsCell(row)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderSuccessfulTable = () => (
    <div className="overflow-x-auto">
      <table className="table-default">
        <thead>
          <tr>
            {renderSortableHeader("Sent At", "date")}
            {renderSortableHeader("Identity ID", "identityId")}
            {renderSortableHeader("Business ID", "businessId")}
            {renderSortableHeader("Tx Timestamp", "txTimestamp")}
            {renderSortableHeader("Card Last 4", "cardLast4")}
            {renderSortableHeader("Amount", "amount")}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row, index) => (
            <tr key={row.item.uniqueTransactionId || index}>
              <td className="whitespace-nowrap">{formatDateTime(row.eventTimestamp)}</td>
              <td className="font-mono text-xs whitespace-nowrap">{row.json.identityId || "-"}</td>
              <td className="font-mono text-xs whitespace-nowrap">{row.json.contentfulBusinessId || "-"}</td>
              <td className="whitespace-nowrap">{formatDateTime(row.json.transactionTimestamp)}</td>
              <td className="font-mono whitespace-nowrap">{row.json.cardNumberLastFour || "-"}</td>
              <td className="whitespace-nowrap">
                {formatAmount(row.json.transactionAmount, row.json.transactionCurrency)}
              </td>
              {renderDetailsCell(row)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Transaction History</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={loading || filteredRows.length === 0}
          >
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <Button
          onClick={() => handleTabChange("successful")}
          variant={activeTab === "successful" ? "primary" : "outline"}
          size="sm"
        >
          Successful {successfulCount !== null ? `(${successfulCount})` : ""}
        </Button>
        <Button
          onClick={() => handleTabChange("failed")}
          variant={activeTab === "failed" ? "primary" : "outline"}
          size="sm"
        >
          Failed {failedCount !== null ? `(${failedCount})` : ""}
        </Button>
      </div>

      {/* Date Range Filter */}
      <div className="flex items-end gap-4 mb-4">
        <div className="w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date (Copenhagen)
          </label>
          <input
            type="date"
            className="w-full p-2 border border-gray-300 rounded-md"
            value={dateFrom}
            onChange={(e) => handleDateFromChange(e.target.value)}
          />
        </div>
        <div className="w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date (Copenhagen)
          </label>
          <input
            type="date"
            className="w-full p-2 border border-gray-300 rounded-md"
            value={dateTo}
            onChange={(e) => handleDateToChange(e.target.value)}
          />
        </div>
        {(dateFrom || dateTo) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              setPage(1);
            }}
          >
            Clear dates
          </Button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center gap-2 py-8">
          <Loader />
          {loadProgress && loadProgress.total > FETCH_PAGE_SIZE && (
            <span className="text-sm text-gray-500">
              Loading transactions… {loadProgress.loaded} / {loadProgress.total}
            </span>
          )}
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
          {error}
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          No {activeTab} transactions found
        </div>
      ) : (
        <>
          {activeTab === "failed" ? renderFailedTable() : renderSuccessfulTable()}

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              {filteredRows.length} transaction{filteredRows.length !== 1 ? "s" : ""}
              {dateFrom || dateTo ? " in range" : " total"}
            </span>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              <span className="text-sm">
                Page {page} of {totalPages || 1}
              </span>
              <Button
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TransactionHistorySection;
